import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AccountingHealth } from "@/api/accounting";
import { exchangePlaidPublicToken, syncPlaidAccount } from "@/api/bank";
import { Button } from "@/components/ui/button";
import { MappingHealthBanner } from "@/components/accounting/MappingHealthBanner";
import { MappingHealthModal } from "@/components/accounting/MappingHealthModal";
import { useAccountingHealth } from "@/hooks/useAccountingHealth";
import { useLedger } from "@/hooks/useLedger";
import { useBanking } from "@/hooks/useBanking";
import type { LedgerAccountInput } from "@/types/ledger";
import { useToast } from "@/components/ToastProvider";
import { useUnifiedErrorSurface } from "@/hooks/useUnifiedErrorSurface";
import { openPlaidLink, type PlaidOnSuccessMetadata } from "@/lib/plaid";

type VenmoPanelProps = {
  health?: AccountingHealth | null;
  showHealthBanner?: boolean;
  healthLoading?: boolean;
  healthError?: string | null;
};

type RecommendedAccount = {
  code: string;
  name: string;
  type: LedgerAccountInput["type"];
  description: string;
  helper: string;
};

const TYPE_LABEL: Record<LedgerAccountInput["type"], string> = {
  asset: "Asset",
  liability: "Liability",
  equity: "Equity",
  income: "Income",
  expense: "Expense",
};

const RECOMMENDED_ACCOUNTS: RecommendedAccount[] = [
  {
    code: "1155",
    name: "Venmo Clearing",
    type: "asset",
    description: "Tracks the balance held in Venmo before you sweep funds into a bank account.",
    helper: "Debit when customers pay you in Venmo; credit when those funds transfer out.",
  },
  {
    code: "4055",
    name: "Venmo Sales",
    type: "income",
    description: "Captures gross sales that settle via Venmo to keep reporting by channel.",
    helper: "Use when classifying deposits so Venmo activity is separated from Shopify or cash.",
  },
  {
    code: "6055",
    name: "Venmo Fees",
    type: "expense",
    description: "Records Venmo processing fees and instant-transfer charges.",
    helper: "Pair with Venmo Clearing so net payouts match the platform statements.",
  },
];

export function VenmoPanel({
  health: externalHealth,
  showHealthBanner = true,
  healthLoading,
  healthError,
}: VenmoPanelProps = {}) {
  const { push } = useToast();
  const {
    accounts,
    createAccount,
    refresh: refreshLedger,
    loading: ledgerLoading,
    error: ledgerError,
    hasArtist,
  } = useLedger();
  const { accounts: bankAccounts, refresh: refreshBank } = useBanking();
  const [creatingCode, setCreatingCode] = useState<string | null>(null);
  const [healthModalOpen, setHealthModalOpen] = useState(false);
  const [venmoLedgerAccountId, setVenmoLedgerAccountId] = useState<number | null>(null);
  const [linkingVenmo, setLinkingVenmo] = useState(false);
  const [syncingAccountId, setSyncingAccountId] = useState<number | null>(null);
  const {
    health: internalHealth,
    loading: internalLoading,
    error: internalError,
  } = useAccountingHealth(!externalHealth);
  const mergedHealth = externalHealth ?? internalHealth;
  const bannerLoading = healthLoading ?? internalLoading;
  const bannerError = healthError ?? internalError;
  const { notifyError } = useUnifiedErrorSurface();

  useEffect(() => {
    if (venmoLedgerAccountId) return;
    const preferred =
      accounts.find((account) => account.code === "1155") ??
      accounts.find((account) => account.code?.startsWith("11"));
    setVenmoLedgerAccountId(preferred?.id ?? accounts[0]?.id ?? null);
  }, [accounts, venmoLedgerAccountId]);

  const existingByCode = useMemo(() => {
    const map = new Map<string, string>();
    accounts.forEach((account) => {
      if (account.code) {
        map.set(account.code, account.name);
      }
    });
    return map;
  }, [accounts]);

  const formatLastSynced = (value?: string | null) => {
    if (!value) return "Never synced";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const venmoAccounts = useMemo(() => bankAccounts.filter((account) => (account.channel ?? "bank") === "venmo"), [bankAccounts]);

  const handleCreateAccount = async (payload: RecommendedAccount) => {
    setCreatingCode(payload.code);
    try {
      await createAccount({
        code: payload.code,
        name: payload.name,
        type: payload.type,
        currency: "USD",
      });
      push({ title: "Account created", message: `${payload.code} • ${payload.name}` });
    } catch (err) {
      notifyError(err);
    } finally {
      setCreatingCode(null);
    }
  };

  const handleVenmoPlaidConnect = async () => {
    if (!venmoLedgerAccountId) {
      push({ title: "Select ledger account", message: "Pick which clearing account should hold Venmo activity." });
      return;
    }
    setLinkingVenmo(true);
    try {
      await openPlaidLink({
        channel: "venmo",
        onExit: () => setLinkingVenmo(false),
        onSuccess: async (publicToken: string, metadata: PlaidOnSuccessMetadata) => {
          const account = metadata.accounts?.[0];
          if (!account) {
            push({ title: "No Venmo account", message: "Select your Venmo business profile in Plaid Link first." });
            setLinkingVenmo(false);
            return;
          }
          try {
            await exchangePlaidPublicToken({
              public_token: publicToken,
              account_id: account.id,
              account_name: account.name,
              account_mask: account.mask,
              institution_id: metadata.institution?.institution_id,
              institution_name: metadata.institution?.name,
              ledger_account_id: venmoLedgerAccountId,
              channel: "venmo",
            });
            push({ title: "Venmo linked", message: account.name ?? "Plaid feed connected" });
            await refreshBank();
          } catch (err) {
            notifyError(err);
          } finally {
            setLinkingVenmo(false);
          }
        },
      });
    } catch (err) {
      setLinkingVenmo(false);
      notifyError(err);
    }
  };

  const handleVenmoSync = async (accountId: number) => {
    setSyncingAccountId(accountId);
    try {
      const result = await syncPlaidAccount(accountId);
      push({
        title: "Venmo synced",
        message: `Imported ${result.imported} new entries (${result.updated} updates).`,
      });
      await refreshBank();
    } catch (err) {
      notifyError(err);
    } finally {
      setSyncingAccountId(null);
    }
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-6">
      {showHealthBanner && (
        <MappingHealthBanner
          health={mergedHealth}
          loading={bannerLoading}
          error={bannerError}
          onViewRequirements={() => setHealthModalOpen(true)}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-primary">Payout channel</p>
          <h2 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Venmo collections</h2>
          <p className="text-sm text-muted-foreground">
            Document Venmo business handles, map ledger accounts, and keep transfers in sync with your books.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" asChild>
            <Link to="/accounting?tab=accounts">Go to accounts</Link>
          </Button>
          <Button variant="secondary" onClick={() => refreshLedger()} disabled={ledgerLoading}>
            {ledgerLoading ? "Refreshing…" : "Refresh ledger"}
          </Button>
        </div>
      </div>

      {!hasArtist && (
        <div className="rounded-2xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          Select an artist profile to load their Venmo mappings and ledger accounts.
        </div>
      )}

      {ledgerError && hasArtist && (
        <div className="rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">{ledgerError}</div>
      )}

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-2xl border border-border bg-background/60 p-5">
          <header>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Connection checklist</h3>
            <p className="text-xs text-muted-foreground">Follow these steps to keep Venmo payouts reconciliation-ready.</p>
          </header>
          <ol className="list-decimal space-y-3 pl-5 text-sm text-foreground/80">
            <li>
              <span className="font-semibold text-primary-foreground dark:text-amber-100">Confirm your Venmo business profile.</span>
              <p className="text-xs text-muted-foreground">
                Capture @handle, contact email, and instant-transfer settings inside your internal runbook.
              </p>
            </li>
            <li>
              <span className="font-semibold text-primary-foreground dark:text-amber-100">Add Venmo-specific ledger accounts.</span>
              <p className="text-xs text-muted-foreground">
                Use the quick actions on this page or jump to the accounts workspace to edit codes manually.
              </p>
            </li>
            <li>
              <span className="font-semibold text-primary-foreground dark:text-amber-100">Record sweeps just like Shopify payouts.</span>
              <p className="text-xs text-muted-foreground">
                When funds hit the bank, journal an entry from Venmo Clearing into your operating account.
              </p>
            </li>
          </ol>
        </div>
        <div className="space-y-4 rounded-2xl border border-border bg-background/60 p-5">
          <header>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Documentation tips</h3>
            <p className="text-xs text-muted-foreground">Give finance and ops the context they need to audit Venmo payouts.</p>
          </header>
          <ul className="space-y-3 text-sm text-foreground/80">
            <li className="rounded-xl border border-border/80 bg-background/30 p-3">
              <p className="font-semibold text-primary-foreground dark:text-amber-100">Statement archive</p>
              <p className="text-xs text-muted-foreground">Save monthly CSV exports from Venmo Business so reconciliations have source docs.</p>
            </li>
            <li className="rounded-xl border border-border/80 bg-background/30 p-3">
              <p className="font-semibold text-primary-foreground dark:text-amber-100">Who can transfer?</p>
              <p className="text-xs text-muted-foreground">List users who can initiate payouts to stay SOX-compliant.</p>
            </li>
            <li className="rounded-xl border border-border/80 bg-background/30 p-3">
              <p className="font-semibold text-primary-foreground dark:text-amber-100">Fee expectations</p>
              <p className="text-xs text-muted-foreground">Document instant-transfer fees vs. standard ACH so you can forecast margin impact.</p>
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-4 rounded-2xl border border-border bg-background/60 p-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Venmo Plaid feeds</h3>
            <p className="text-xs text-muted-foreground">Link your Venmo business profile and pull deposits directly into the ledger.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <select
              className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              value={venmoLedgerAccountId ?? ""}
              onChange={(event) => setVenmoLedgerAccountId(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">Ledger account</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.code} · {account.name}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={handleVenmoPlaidConnect} disabled={linkingVenmo || !venmoLedgerAccountId || ledgerLoading}>
              {linkingVenmo ? "Launching…" : "Link Venmo via Plaid"}
            </Button>
          </div>
        </header>
        {venmoAccounts.length === 0 ? (
          <p className="text-xs text-muted-foreground">No Venmo accounts linked yet. Connect a profile to start syncing deposits.</p>
        ) : (
          <div className="space-y-3 text-sm text-foreground/80">
            {venmoAccounts.map((account) => (
              <div key={account.id} className="rounded-xl border border-border bg-background/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-primary-foreground dark:text-amber-100">{account.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {account.connection_provider_name ?? account.institution ?? "Venmo"} &middot; {formatLastSynced(account.connection_last_synced_at)}
                    </p>
                  </div>
                  {account.connection_provider === "plaid" ? (
                    <Button size="sm" variant="secondary" onClick={() => handleVenmoSync(account.id)} disabled={syncingAccountId === account.id}>
                      {syncingAccountId === account.id ? "Syncing…" : "Sync now"}
                    </Button>
                  ) : (
                    <span className="rounded-full border border-border px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">Manual</span>
                  )}
                </div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {account.connection_provider_status ?? account.connection_provider ?? "connected"}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-2xl border border-border bg-background/60 p-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Recommended ledger accounts</h3>
            <p className="text-xs text-muted-foreground">Create or review the Venmo-specific accounts that power reporting.</p>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/accounting?tab=accounts">Open chart of accounts</Link>
          </Button>
        </header>
        <div className="grid gap-4 md:grid-cols-2">
          {RECOMMENDED_ACCOUNTS.map((account) => {
            const exists = existingByCode.has(account.code);
            return (
              <div key={account.code} className="space-y-3 rounded-xl border border-border bg-background/30 p-4 text-sm text-foreground/80">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-foreground">
                      {account.name} <span className="text-xs text-muted-foreground">({account.code})</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {TYPE_LABEL[account.type]} account &middot; {account.helper}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                      exists ? "bg-success/20 text-success" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {exists ? "Linked" : "Missing"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{account.description}</p>
                {exists ? (
                  <Button variant="secondary" size="sm" asChild>
                    <Link to="/accounting?tab=accounts">View accounts</Link>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleCreateAccount(account)}
                    disabled={creatingCode === account.code || !hasArtist}
                  >
                    {creatingCode === account.code ? "Creating…" : "Create account"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <MappingHealthModal
        open={healthModalOpen}
        health={mergedHealth ?? null}
        onClose={() => setHealthModalOpen(false)}
        ctaHref="/accounting?tab=accounts"
        ctaLabel="Review chart of accounts"
      />
    </div>
  );
}
